import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { getBlogPosts, type BlogPost } from "../utils/adminStore";
import { PageHero } from "../components/PageHero";

export function BlogDirectory() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    const sync = () => setPosts(getBlogPosts());
    sync();
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        post.tags.some((t) => t.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [posts, selectedCategory, searchQuery]);

  const featuredPost = useMemo(() => {
    return posts.find((p) => p.featured) || posts[0];
  }, [posts]);

  return (
    <>
      <PageHero
        badge="Engineering Publications & Insights"
        title="Technical Blog & Systems Architecture"
        description="Deep dives into enterprise software architecture, embedded IoT hardware telemetry, DPDP compliance, and production robotics from the RADIECE engineering team."
      />

      <section className="bg-slate-50 dark:bg-[#070b12] py-12 sm:py-16 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Controls: Search & Category Filter */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-10">
            {/* Category Chips */}
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                    selectedCategory === cat
                      ? "bg-brand text-white dark:bg-primary dark:text-slate-950 shadow-md"
                      : "bg-white dark:bg-[#0e1724] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:border-brand/40"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="w-full md:w-72">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles, tags, tech..."
                className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-[#0e1724] border border-slate-200 dark:border-slate-800 text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-brand dark:focus:border-primary shadow-xs"
              />
            </div>
          </div>

          {/* Featured Post Card (When no search active) */}
          {!searchQuery && selectedCategory === "All" && featuredPost && (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12 bg-gradient-to-br from-[#0b131e] to-[#101c2a] text-white border border-slate-800 rounded-2xl p-8 sm:p-10 shadow-2xl relative overflow-hidden group"
            >
              <div className="max-w-3xl relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-primary/20 border border-primary/30 text-primary text-xs font-bold rounded-full uppercase tracking-wider">
                    ★ Featured Technical Publication
                  </span>
                  <span className="text-xs font-mono text-slate-400">
                    {featuredPost.readingTime}
                  </span>
                </div>

                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 leading-tight group-hover:text-primary transition-colors">
                  <a href={`/blog/${featuredPost.slug}`}>
                    {featuredPost.title}
                  </a>
                </h2>

                <p className="text-sm sm:text-base text-slate-300 leading-relaxed font-light mb-6">
                  {featuredPost.excerpt}
                </p>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-xs">
                      {featuredPost.author.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-200">{featuredPost.author.name}</p>
                      <p className="text-[11px] text-slate-400">{featuredPost.author.role}</p>
                    </div>
                  </div>

                  <a
                    href={`/blog/${featuredPost.slug}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-sky-400 text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md"
                  >
                    Read Full Publication →
                  </a>
                </div>
              </div>
            </motion.div>
          )}

          {/* Blog Post Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {filteredPosts.map((post, idx) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: idx * 0.05 }}
                className="card-interactive bg-white dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-7 flex flex-col justify-between hover:border-brand/40 dark:hover:border-primary/40 shadow-xs transition-colors group"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-brand dark:text-primary">
                      {post.category}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      {post.readingTime}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-brand-dark dark:text-slate-100 mb-3 group-hover:text-brand dark:group-hover:text-primary transition-colors leading-snug">
                    <a href={`/blog/${post.slug}`}>
                      {post.title}
                    </a>
                  </h3>

                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-light leading-relaxed mb-6">
                    {post.excerpt}
                  </p>
                </div>

                <div>
                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {post.tags.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded"
                      >
                        #{t}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800/80 text-xs">
                    <span className="text-slate-400 font-mono">{post.publishedAt}</span>
                    <a
                      href={`/blog/${post.slug}`}
                      className="font-bold text-brand dark:text-primary hover:text-primary dark:hover:text-sky-300 transition-colors uppercase tracking-wider inline-flex items-center gap-1"
                    >
                      Read Article →
                    </a>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16 bg-white dark:bg-[#0e1724] rounded-2xl border border-slate-200 dark:border-slate-800 p-8 max-w-md mx-auto">
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
                No publications match your search criteria.
              </p>
              <button
                onClick={() => {
                  setSelectedCategory("All");
                  setSearchQuery("");
                }}
                className="px-4 py-2 bg-brand text-white dark:bg-primary dark:text-slate-950 rounded-lg text-xs font-bold uppercase cursor-pointer"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
